/**
 * VA & Avlopp – separat kategori för vatten, avlopp och brunnar.
 * Detta är ett populärt sökområde (enskilt avlopp, dricksvattenbrunn m.fl.)
 * som ligger utanför bygglov men ofta krävs tillsammans med byggprojekt.
 */
export const vaRules = [
  {
    slug: "enskilt-avlopp",
    title: "Enskilt avlopp – tillstånd och regler",
    h1: "Enskilt avlopp – ansök om tillstånd, kostnad och alternativ",
    metaDescription: "Nytt enskilt avlopp kräver tillstånd av kommunens miljökontor. Kostnad 3 000-8 000 kr. Regler för infiltration, minireningsverk 2026.",
    keywords: "enskilt avlopp tillstånd, infiltration, minireningsverk",
    intro: "Enskilt avlopp (för fastigheter utan kommunalt VA) kräver tillstånd av kommunens miljöförvaltning enligt Miljöbalken 9 kap. och Förordningen om miljöfarlig verksamhet (1998:899).",
    measurements: [
      { label: "Handläggning", value: "6-12 veckor från komplett ansökan" },
      { label: "Avgift", value: "3 000 – 8 000 kr" },
      { label: "Krav", value: "Skyddsnivå (normal eller hög) beroende på området" },
      { label: "Livslängd", value: "Infiltration 20-25 år, minireningsverk 15-20 år" },
    ],
    examples: [
      { type: "Villa", title: "Nytt fritidshus i skärgård", text: "Minireningsverk med hög skyddsnivå + kompletterande efterpolering krävs vanligen." },
      { type: "Villa", title: "Ersätta gammal trekammarbrunn", text: "Tillstånd krävs för ny anläggning – gamla infiltrationer räknas inte som godkända." },
    ],
    mistakes: [
      "Att gräva ner ny anläggning innan tillstånd är beviljat – förbjudet enligt Miljöbalken.",
      "Att välja infiltration i lera – fungerar inte, kräver markbädd eller minireningsverk.",
    ],
    municipality_variations: "Kustkommuner och Mälardalen har oftast hög skyddsnivå med striktare krav.",
    faq: [
      { q: "Hur länge håller ett minireningsverk?", a: "15-20 år med regelbunden service." },
      { q: "Kan jag installera själv?", a: "Grävning ja, men diplomerad entreprenör krävs för anläggningens funktion." },
    ],
    related: ["va/dricksvattenbrunn", "va/markarbete-va", "va/sluten-tank"],
    updated: "2026-07-04",
  },
  {
    slug: "dricksvattenbrunn",
    title: "Dricksvattenbrunn – borrning och regler",
    h1: "Egen brunn – borrning, tillstånd och vattenprov",
    metaDescription: "Egen dricksvattenbrunn kräver oftast anmälan (inte tillstånd). Kostnad 60 000-120 000 kr. Vattenprov varje 3-5 år. Regler 2026.",
    keywords: "dricksvattenbrunn, borrad brunn, vattenprov villa",
    intro: "Att borra egen dricksvattenbrunn kräver inte tillstånd utan endast anmälan i de flesta kommuner. Om brunnen ligger i vattenskyddsområde eller nära föroreningar krävs tillstånd.",
    measurements: [
      { label: "Kostnad borrning", value: "60 000 – 120 000 kr (7-15 km borrdjup vanligt)" },
      { label: "Anmälan", value: "Krävs enligt Miljöbalken före borrning" },
      { label: "Vattenprov", value: "Rekommenderas var 3-5 år" },
    ],
    examples: [
      { type: "Villa", title: "Ny bergborrad brunn", text: "Anmälan → borrning → provpumpning → vattenanalys → installation." },
      { type: "Villa", title: "Vattenskyddsområde", text: "Tillstånd krävs – prövas mot skyddsföreskrifter." },
    ],
    mistakes: [
      "Att inte göra vattenanalys innan installation – hälsofarligt vatten upptäcks sent.",
      "Att glömma anmäla brunnen till SGU (Sveriges geologiska undersökning).",
    ],
    municipality_variations: "Vissa kommuner har helt egna brunnsregister och riktvärden.",
    faq: [
      { q: "Vad kostar vattenprov?", a: "800-2 500 kr för normal analys (mikrobiologisk + kemisk)." },
      { q: "Får jag borra brunn på grannens tomt?", a: "Nej – kräver servitut eller överenskommelse." },
    ],
    related: ["va/enskilt-avlopp", "va/markarbete-va"],
    updated: "2026-07-04",
  },
  {
    slug: "sluten-tank",
    title: "Sluten tank – när tillåts det?",
    h1: "Sluten tank – enkelt tillfälligt avlopp",
    metaDescription: "Sluten tank är ett enkelt avloppsalternativ när infiltration inte fungerar. Krav på tömningsschema, kostnad och regler 2026.",
    keywords: "sluten tank avlopp, WC-tank, avloppstank",
    intro: "Sluten tank för toalettvatten är ett alternativ när mark inte tillåter infiltration eller minireningsverk. Kräver tillstånd av kommunens miljökontor och regelbunden tömning.",
    measurements: [
      { label: "Volym", value: "Normalt 3 000 – 6 000 liter" },
      { label: "Tömningsfrekvens", value: "1-4 gånger per år" },
      { label: "Tömningskostnad", value: "1 500 – 3 000 kr per tömning" },
      { label: "Livslängd", value: "20-30 år (glasfiber/plast)" },
    ],
    examples: [
      { type: "Villa", title: "Fritidshus med snålspol-WC", text: "Sluten tank för WC + BDT-avlopp separat via markbädd." },
    ],
    mistakes: [
      "Att glömma teckna tömningsavtal med kommunens renhållning.",
      "Att välja tank för liten – ger onödigt frekventa tömningar.",
    ],
    municipality_variations: "Vissa kommuner tillåter sluten tank endast som tillfällig lösning i väntan på annan anläggning.",
    faq: [
      { q: "Kan jag tömma tanken själv?", a: "Nej – kommunens renhållning eller godkänd slamsug krävs." },
    ],
    related: ["va/enskilt-avlopp", "va/dricksvattenbrunn"],
    updated: "2026-07-04",
  },
  {
    slug: "markarbete-va",
    title: "Markarbete och schaktning för VA",
    h1: "Schaktning för VA – tillstånd och regler",
    metaDescription: "Grävning för avloppsledningar, dagvatten och kablar kan kräva marklov, ledningsrätt eller schakttillstånd. Regler 2026.",
    keywords: "schaktning VA, gräva för avlopp, marklov ledning",
    intro: "Grävning för VA-ledningar och kablar är oftast bygglovsfri men kan kräva schakttillstånd om det påverkar allmän mark. Ledningsrätt krävs vid grävning över grannens tomt.",
    measurements: [
      { label: "Djup avloppsledning", value: "0,7 – 1,2 m (frostfritt djup)" },
      { label: "Fall", value: "Min 1:100 för självfall" },
      { label: "Marklov", value: "Krävs om markändring > 0,5 m i detaljplan" },
    ],
    examples: [
      { type: "Villa", title: "Egen tomt", text: "Grävning för VA fritt – men kalla kabelanvisning (Ledningskollen.se) först." },
      { type: "Villa", title: "Genom grannens tomt", text: "Servitut eller ledningsrätt krävs – bevakas av Lantmäteriet." },
    ],
    mistakes: [
      "Att gräva utan att ringa Ledningskollen först – risk att skada fiber/el/gas.",
      "Att glömma ansöka om ledningsrätt vid grävning över annans mark.",
    ],
    municipality_variations: "Kommuner har olika krav på återställning av grävda gator (asfalt, markering).",
    faq: [
      { q: "Kostar det att ringa Ledningskollen?", a: "Nej, helt gratis och lagkrav." },
    ],
    related: ["va/enskilt-avlopp", "bygglov/marklov"],
    updated: "2026-07-04",
  },
];
